import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";

const ACTIVATE_DELAY = 0;
/** Delay before switching between open desktop flyouts (ms) — prevents diagonal pointer travel from stealing the menu */
const SWITCH_ACTIVATE_DELAY = 250;
/** Grace period after pointer leaves before closing (ms) — eases moving into the flyout without snapping shut */
const DEACTIVATE_DELAY = 500;

/**
 * Desktop nav menu component.
 * Uses on:pointerenter/on:pointerleave declarative events from Component base class.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu panels.
 *
 * @extends {Component<Refs>}
 */
class NavMenu extends Component {
  _state = { activeItem: null };

  /** @type {number | undefined} */
  #activateTimeout;

  get #inDrawer() {
    return this.hasAttribute("data-nav-menu-in-drawer") && !this.hasAttribute("data-mobile-drawer-nav");
  }

  get animationDelay() {
    return parseInt(this.dataset.animationDelay || "0", 10);
  }

  /** Recalculate flyout top after resize while a panel is open; re-center drawer tab strip */
  #onFlyoutViewportResize = () => {
    const menuitem = this._state.activeItem;
    if (!menuitem) return;

    const navItem = menuitem.closest(".nav-menu__item");
    if (!navItem) return;

    if (this.#inDrawer) {
      this.#syncDrawerChromeTop();
      this.#centerDrawerActiveTab(navItem, { instant: true });
      return;
    }

    const submenu = navItem.querySelector('[ref="submenu[]"]');
    if (submenu instanceof HTMLElement) this.#syncFlyoutViewportTop(submenu);
  };

  /**
   * Scroll the horizontal top-level strip so the open drawer item is centered.
   * Animation comes from CSS `scroll-behavior: smooth` on `.nav-menu-drawer__tabs-scroll`
   * plus `behavior: "auto"` here (see CSSOM; instant skips animation for resize).
   * @param {HTMLElement} navItem - `.nav-menu__item` li
   * @param {{ instant?: boolean }} [options]
   */
  #centerDrawerActiveTab = (navItem, options = {}) => {
    if (!(navItem instanceof HTMLElement)) return;
    const scroller = navItem.closest(".nav-menu-drawer__tabs-scroll");
    if (!(scroller instanceof HTMLElement)) return;
    const behavior = options.instant ? "instant" : "auto";
    navItem.scrollIntoView({ inline: "center", block: "nearest", behavior });
  };

  /**
   * @param {HTMLElement} submenu
   */
  #syncFlyoutViewportTop(submenu) {
    const top = Math.max(0, Math.round(submenu.getBoundingClientRect().top));
    this.style.setProperty("--nav-menu-flyout-top-offset", `${top}px`);
  }

  #syncDrawerChromeTop() {
    const tabBar = this.querySelector(".nav-menu-drawer__tab-bar");
    const drawer = this.closest(".menu-drawer");
    if (!tabBar || !drawer) return;
    const chromeTop = Math.round(tabBar.getBoundingClientRect().bottom - drawer.getBoundingClientRect().top);
    drawer.style.setProperty("--nav-drawer-chrome-top", `${chromeTop}px`);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("click", this.#onDrawerRowToggle, true);

    if (!this.#inDrawer) return;

    this.addEventListener("click", this.#onDrawerClick);

    const scroller = this.querySelector(".nav-menu-drawer__tabs-scroll");
    if (scroller) {
      scroller.addEventListener("scroll", this.#onTabsScroll, { passive: true });
      this.#onTabsScroll({ target: scroller });
    }

    const details = this.closest("details");
    if (details instanceof HTMLDetailsElement) {
      details.addEventListener("toggle", this.#onDrawerDetailsToggle);
    }
  }

  disconnectedCallback() {
    this.#cancelDelayedActivate();

    const details = this.closest("details");
    if (details instanceof HTMLDetailsElement) {
      details.removeEventListener("toggle", this.#onDrawerDetailsToggle);
    }
    const scroller = this.querySelector(".nav-menu-drawer__tabs-scroll");
    if (scroller) scroller.removeEventListener("scroll", this.#onTabsScroll);
    this.removeEventListener("click", this.#onDrawerClick);
    this.removeEventListener("click", this.#onDrawerRowToggle, true);

    super.disconnectedCallback();
  }

  #onTabsScroll = (event) => {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;
    const wrapper = el.closest(".fade-overflow-x");
    if (!wrapper) return;
    const atStart = el.scrollLeft <= 2;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    wrapper.dataset.fadeStart = String(!atStart);
    wrapper.dataset.fadeEnd = String(!atEnd);
  };

  #onDrawerDetailsToggle = () => {
    const details = this.closest("details");
    if (details instanceof HTMLDetailsElement && !details.open) {
      this.#closeActiveSubmenu();
      this.#hideTabbedItems();
    }
  };

  /** Public method for declarative on:click on back button */
  closeDrawerSubmenu() {
    if (this._state.tabbedItems) {
      this.#closeActiveSubmenu();
      this.#hideTabbedItems();
      return;
    }
    this.#closeActiveSubmenu();
  }

  #closeActiveSubmenu() {
    const item = this._state.activeItem;
    if (!item) return;

    window.removeEventListener("resize", this.#onFlyoutViewportResize);
    this.style.removeProperty("--nav-menu-flyout-top-offset");
    this.style.setProperty("--submenu-height", "0px");
    this.style.setProperty("--submenu-opacity", "0");
    this._state.activeItem = null;
    item.setAttribute("aria-expanded", "false");
  }

  #onDrawerRowToggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const row = event.target.closest("[data-nav-row-toggle]");
    if (!row || !this.contains(row)) return;

    const childrenId = row.getAttribute("aria-controls");
    if (!childrenId) return;

    event.preventDefault();
    event.stopPropagation();

    let children = row.nextElementSibling;
    const isAdjacentPanel =
      children instanceof HTMLElement &&
      (children.classList.contains("nav-menu__row__children") ||
        children.classList.contains("nav-menu-content__children"));
    if (!isAdjacentPanel) {
      children = this.querySelector(`#${CSS.escape(childrenId)}`);
    }
    if (!children) {
      children = document.getElementById(childrenId);
    }
    if (!children) return;

    if (row.getAttribute("aria-expanded") === "true") {
      children.removeAttribute("data-expanded");
      row.setAttribute("aria-expanded", "false");
    } else {
      children.setAttribute("data-expanded", "");
      row.setAttribute("aria-expanded", "true");
    }
  };

  #onDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[ref="menuitem"][aria-haspopup="true"]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();

    const navItem = trigger.closest(".nav-menu__item");
    if (!navItem) return;

    const isOpen = this._state.activeItem === trigger;

    if (isOpen) {
      this.#closeActiveSubmenu();
      return;
    }

    if (navItem.getAttribute("data-menu-type") === "tabbed") {
      this.#openTabbedMenu(navItem);
      return;
    }

    if (!navItem.hasAttribute("data-tab-of")) {
      this.#hideTabbedItems();
    }
    this._debouncedDeactivate.cancel();
    this._debouncedActivate.cancel();
    this.#applyOpenState(trigger, navItem);
  };

  #openTabbedMenu(navItem) {
    this.#closeActiveSubmenu();
    const blockId = navItem.dataset.blockId;
    const tabItems = blockId
      ? this.querySelectorAll(`[data-tab-of="${blockId}"]`)
      : this.querySelectorAll("[data-tab-of]");
    if (tabItems.length === 0) return;
    this.#showTabbedItems(tabItems);
  }

  #showTabbedItems(tabItems) {
    this._state.tabbedItems = tabItems;
    for (const item of this.querySelectorAll(".nav-menu__item:not(.nav-menu__item--tab)")) {
      item.hidden = true;
    }
    for (const tab of tabItems) {
      tab.hidden = false;
    }
    const firstTrigger = tabItems[0]?.querySelector('[ref="menuitem"]');
    if (firstTrigger) {
      const firstItem = firstTrigger.closest(".nav-menu__item");
      this._debouncedDeactivate.cancel();
      this._debouncedActivate.cancel();
      this.#applyOpenState(firstTrigger, firstItem);
    }
  }

  #hideTabbedItems() {
    if (!this._state.tabbedItems) return;
    for (const tab of this._state.tabbedItems) {
      tab.hidden = true;
    }
    for (const item of this.querySelectorAll(".nav-menu__item:not(.nav-menu__item--tab)")) {
      item.hidden = false;
    }
    this._state.tabbedItems = null;
  }

  #applyOpenState(menuitem, navItem) {
    const previous = this._state.activeItem;
    if (previous) {
      previous.setAttribute("aria-expanded", "false");
    }

    this._state.activeItem = menuitem;
    menuitem.setAttribute("aria-expanded", "true");

    window.removeEventListener("resize", this.#onFlyoutViewportResize);
    window.addEventListener("resize", this.#onFlyoutViewportResize, { passive: true });

    const submenu = navItem.querySelector('[ref="submenu[]"]');
    const fullHeight = submenu ? submenu.scrollHeight : 0;

    this.style.setProperty("--submenu-height", fullHeight + "px");
    this.style.setProperty("--submenu-opacity", "1");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.#inDrawer) this.#syncDrawerChromeTop();
        if (submenu instanceof HTMLElement) this.#syncFlyoutViewportTop(submenu);
        this.#centerDrawerActiveTab(navItem);
      });
    });
  }

  toggleDesktopSubmenu = (event) => {
    if (this.#inDrawer) return;
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest(".nav-menu__item");
    if (!item) return;

    const menuitem = item.querySelector('[ref="menuitem"]');
    if (!menuitem) return;

    /*
     * iPadOS and many tablets report (hover: hover) like a desktop, so (hover: none) alone misses them.
     * Without preventDefault, the first tap follows <a href> instead of pinning the mega menu open.
     */
    const prefersTapMenuOverLinkNavigation =
      matchMedia("(hover: none)").matches ||
      matchMedia("(pointer: coarse)").matches ||
      matchMedia("(any-pointer: coarse)").matches;

    if (!prefersTapMenuOverLinkNavigation) return;

    event.preventDefault();

    if (menuitem === this._state.activeItem && this._state.pinned) {
      this._state.pinned = false;
      this._debouncedDeactivate.cancel();
      this._deactivateHandler(menuitem);
      document.removeEventListener("click", this.#onDocumentClickWhilePinned, true);
      return;
    }

    this._debouncedDeactivate.cancel();
    this._debouncedActivate.cancel();
    this._state.pinned = true;

    if (menuitem !== this._state.activeItem) {
      this._activateHandler(event);
    }

    requestAnimationFrame(() => {
      document.addEventListener("click", this.#onDocumentClickWhilePinned, true);
    });
  };

  #onDocumentClickWhilePinned = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest("nav-menu") === this) return;
    this._state.pinned = false;
    this._deactivateHandler();
    document.removeEventListener("click", this.#onDocumentClickWhilePinned, true);
  };

  activate = (event) => {
    if (this.#inDrawer) return;

    this._debouncedDeactivate.cancel();
    this._debouncedActivate.cancel();
    this.#cancelDelayedActivate();

    if (!(event.target instanceof Element)) return;
    const item = event.target.closest(".nav-menu__item");
    const menuitem = item?.querySelector('[ref="menuitem"]');
    if (!menuitem && this._state.activeItem) {
      this._state.pinned = false;
      this._deactivateHandler();
      document.removeEventListener("click", this.#onDocumentClickWhilePinned, true);
      return;
    }

    if (event.type === "pointerenter" && this._state.activeItem) {
      if (menuitem !== this._state.activeItem) {
        this.#activateTimeout = window.setTimeout(() => {
          this._activateHandler(event);
        }, SWITCH_ACTIVATE_DELAY);
        return;
      }
    }

    this._debouncedActivate(event);
  };

  _activateHandler = (event) => {
    this._debouncedDeactivate.cancel();

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest(".nav-menu__item");
    if (!item) return;

    const menuitem = item.querySelector('[ref="menuitem"]');
    if (!menuitem || menuitem === this._state.activeItem) return;

    this._state.pinned = false;

    const previous = this._state.activeItem;
    if (previous) {
      previous.setAttribute("aria-expanded", "false");
    }

    this._state.activeItem = menuitem;
    menuitem.setAttribute("aria-expanded", "true");

    // Desktop flyout: do not lock body scroll — overflow:hidden removes the scrollbar and reflows the header bar.

    window.removeEventListener("resize", this.#onFlyoutViewportResize);
    window.addEventListener("resize", this.#onFlyoutViewportResize, { passive: true });

    const submenu = item.querySelector('[ref="submenu[]"]');
    const fullHeight = submenu ? submenu.scrollHeight : 0;

    this.style.setProperty("--submenu-height", fullHeight + "px");
    this.style.setProperty("--submenu-opacity", "1");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (submenu instanceof HTMLElement) this.#syncFlyoutViewportTop(submenu);
      });
    });
  };

  _debouncedActivate = debounce(this._activateHandler, ACTIVATE_DELAY);

  deactivate = (event) => {
    if (this.#inDrawer) return;
    if (this._state.pinned) return;

    this._debouncedActivate.cancel();
    this.#cancelDelayedActivate();

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest(".nav-menu__item");
    if (!item) return;

    const menuitem = item.querySelector('[ref="menuitem"]');
    if (menuitem === this._state.activeItem) {
      this._debouncedDeactivate();
    }
  };

  _deactivateHandler = (item = this._state.activeItem) => {
    if (!item || item !== this._state.activeItem) return;
    this._state.pinned = false;

    window.removeEventListener("resize", this.#onFlyoutViewportResize);
    this.style.removeProperty("--nav-menu-flyout-top-offset");

    this.style.setProperty("--submenu-height", "0px");
    this.style.setProperty("--submenu-opacity", "0");

    this._state.activeItem = null;
    item.setAttribute("aria-expanded", "false");

    item.setAttribute("data-animating", "");

    setTimeout(
      () => {
        item.removeAttribute("data-animating");
      },
      Math.max(0, this.animationDelay - 150)
    );
  };

  _debouncedDeactivate = debounce(this._deactivateHandler, DEACTIVATE_DELAY);

  #cancelDelayedActivate() {
    window.clearTimeout(this.#activateTimeout);
    this.#activateTimeout = undefined;
  }
}

if (!customElements.get("nav-menu")) {
  customElements.define("nav-menu", NavMenu);
}

/**
 * Lightweight tab switcher for the tabbed nav menu.
 * Handles click events on [data-nav-tab] buttons and swaps
 * aria-selected / data-active on the corresponding panels.
 */
class NavMenuTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
  }

  #onClick = (event) => {
    const tab = event.target.closest("[data-nav-tab]");
    if (!tab) return;

    const panelId = tab.getAttribute("aria-controls");
    if (!panelId) return;

    for (const sibling of this.querySelectorAll("[data-nav-tab]")) {
      sibling.setAttribute("aria-selected", "false");
    }
    tab.setAttribute("aria-selected", "true");

    for (const panel of this.querySelectorAll("[role='tabpanel']")) {
      panel.removeAttribute("data-active");
    }

    const target = this.querySelector(`#${panelId}`);
    if (target) target.setAttribute("data-active", "");
  };
}

if (!customElements.get("nav-menu-tabs")) {
  customElements.define("nav-menu-tabs", NavMenuTabs);
}
